/**
 * UC-B-2 (UI-14, D3; Final PRD §19–21, §23, §26) — group mini-Gantt from the S3 group read-model: each row is a member
 * participant service period with that participant's coverage drawn over it (worker lanes, open hatched), followed by the
 * group ratio strip (planned slices; actual slices when delivered). Minutes relative to the window date's midnight. Pure.
 */
import { shiftSpan, toMinutes, plusOne } from './shiftSheetRules';
import { groupOffset } from './timelineTracks';

const dayOrdinal = (iso) => Math.floor(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86400000);

export function buildGroupGantt(group, conflicts = []) {
  const w = group?.window || {};
  const base = { date: w.date };
  const off = groupOffset(group, base);
  const rel = (abs) => (off == null ? 0 : abs - off);
  const start = w.start_abs != null ? rel(w.start_abs) : (toMinutes(w.start_time) ?? 0);
  const end = w.end_abs != null ? rel(w.end_abs) : start;
  const assigned = (group?.worker_assignments || []).filter((a) => a.status === 'ASSIGNED' && a.worker_id).map((a) => ({ ...a, s: rel(a.start_abs), e: rel(a.end_abs) }));
  const conflictFor = (wid) => (conflicts || []).find((c) => String(c.worker_id) === String(wid));

  const rows = (group?.members || []).map((m) => {
    const span = shiftSpan(m);
    const days = m.date && w.date ? dayOrdinal(m.date) - dayOrdinal(w.date) : 0;
    const ms = span.start + days * 1440; const me = span.end + days * 1440;
    const lanes = new Map();
    assigned.forEach((a) => {
      const s = Math.max(ms, a.s); const e = Math.min(me, a.e);
      if (e <= s) return;
      const lane = lanes.get(String(a.worker_id)) || { worker_id: String(a.worker_id), label: a.worker_name || `Worker #${String(a.worker_id).slice(-4)}`, conflict: conflictFor(a.worker_id) || null, blocks: [] };
      lane.blocks.push({ start: s, end: e, kind: lane.conflict ? 'conflict' : 'worker', label: a.role && a.role !== 'PRIMARY' ? a.role : '' });
      lanes.set(String(a.worker_id), lane);
    });
    const covered = assigned.map((a) => [Math.max(ms, a.s), Math.min(me, a.e)]).filter(([s, e]) => e > s).sort((a, b) => a[0] - b[0]);
    const open = [];
    let cursor = ms;
    covered.forEach(([s, e]) => { if (s > cursor) open.push({ start: cursor, end: s, kind: 'open', label: 'Open' }); cursor = Math.max(cursor, e); });
    if (cursor < me) open.push({ start: cursor, end: me, kind: 'open', label: 'Open' });
    return { shift_id: String(m.id), participant_id: m.participant_id, label: m.participant_name || `Participant #${String(m.participant_id).slice(-4)}`, start: ms, end: me,
      period: `${plusOne(ms)}–${plusOne(me)}`, lanes: Array.from(lanes.values()), open };
  });

  const strip = (slices) => (slices || []).map((sl) => ({ start: rel(sl.start_abs), end: rel(sl.end_abs), ratio: sl.ratio, label: sl.ratio === 'OPEN' ? 'Open' : sl.ratio === 'UNCOVERED' ? 'Uncovered' : sl.ratio,
    tone: sl.ratio === 'OPEN' || sl.ratio === 'UNCOVERED' ? 'danger' : /^[2-9]:1$/.test(sl.ratio) ? 'accent' : 'neutral', participants: (sl.participant_ids || []).length, workers: (sl.worker_ids || []).length }));
  const axisStart = Math.min(start, ...rows.map((r) => r.start)); const axisEnd = Math.max(end, ...rows.map((r) => r.end));
  const ticks = [];
  for (let t = Math.floor(axisStart / 60) * 60; t <= axisEnd; t += 60) ticks.push({ at: t, label: plusOne(t) });
  return { axis: { start: axisStart, end: axisEnd, ticks }, rows, planned: strip(group?.ratio_slices), actual: strip(group?.actual_ratio_slices) };
}

export const groupSummary = (group) => {
  const workers = new Set((group?.worker_assignments || []).filter((a) => a.status === 'ASSIGNED' && a.worker_id).map((a) => String(a.worker_id)));
  return { participants: (group?.members || []).length, workers: workers.size, typeLabel: group?.delivery_type === 'SIL_HOUSE' ? 'SIL house' : 'Non-SIL shared',
    window: group?.window ? `${group.window.start_time}–${group.window.end_time}${group.window.end_date && group.window.end_date !== group.window.date ? ' +1' : ''}` : '' };
};
